import { Box, Typography } from "@mui/material";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import MenuToggle from "./MenuToggle";
import Lottie from "lottie-react";
import starsLottie from "../misc/lotti/Magic Particles.json";
import LanguageSwitcher from "../i18n/LanguageSwitcher";

const menuVariants = {
  hidden: { y: "-100%", opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    y: "-100%",
    opacity: 0,
    transition: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
  },
};

export default function NavbarMobile() {
  const { t } = useTranslation("navigationmobile");
  const [open, setOpen] = useState(false);

  const closeMenu = () => setOpen(false);

  return (
    <>
      {/* Mobile Top Bar */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          px: 3,
          py: 2,
          display: { xs: "flex", md: "none" },
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(14px)",
          zIndex: 4000,
        }}
      >
        {/* Logo */}
        <NavLink to="/" style={{ textDecoration: "none" }}>
          <Typography
            sx={{
              fontFamily: "Playfair Display",
              fontSize: "0.9rem",
              letterSpacing: "0.3em",
              color: "#111",
            }}
          >
            HELVIETA
          </Typography>
        </NavLink>

        {/* Right Controls: Language + Menu Toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <LanguageSwitcher /> {/* Display selected language here */}
          <MenuToggle open={open} onToggle={() => setOpen(!open)} />
        </Box>
      </Box>

      {/* Fullscreen Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "#F6F5F3",
              zIndex: 3999,
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: "100%",
                px: 3,
                pt: 12,
                pb: 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly",
                gap: 4,
              }}
            >
              {/* Top Row */}
              <Box sx={{ display: "flex", gap: 3 }}>
                <NavBox
                  to="/services"
                  title={t("services.title")}
                  subtitle={t("services.subtitle")}
                  onClick={closeMenu}
                />
                <NavBox
                  to="/fan-access"
                  title={t("fanAccess.title")}
                  subtitle={t("fanAccess.subtitle")}
                  onClick={closeMenu}
                />
              </Box>

              {/* Center Feature */}
              <NavBox
                to="/dancing-with-the-stars"
                title={t("dwts.title")}
                subtitle={t("dwts.subtitle")}
                special
                lottie
                onClick={closeMenu}
              />

              {/* Bottom Row */}
              <Box sx={{ display: "flex", gap: 3 }}>
                <NavBox
                  to="/contact"
                  title={t("contact.title")}
                  subtitle={t("contact.subtitle")}
                  onClick={closeMenu}
                />
                <NavBox
                  to="/partners"
                  title={t("partners.title")}
                  subtitle={t("partners.subtitle")}
                  onClick={closeMenu}
                />
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function NavBox({ title, subtitle, special, lottie, to, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={{ textDecoration: "none", flex: 1 }}
    >
      <motion.div
        whileHover={{ y: -6 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{ height: "100%" }}
      >
        <Box
          sx={{
            height: "100%",
            minHeight: special ? 260 : 150,
            p: 3,
            borderRadius: 3,
            background: special
              ? "linear-gradient(160deg, #181818 0%, #232323 55%, #303030 100%)"
              : "#ECEBE6",
            boxShadow: special
              ? "0 30px 60px rgba(0,0,0,0.14)"
              : "0 14px 28px rgba(0,0,0,0.1)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {special && lottie && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                pointerEvents: "none",
                opacity: 0.25,
                mixBlendMode: "screen",
              }}
            >
              <Lottie animationData={starsLottie} loop autoplay />
            </Box>
          )}

          <Box position="relative" zIndex={1}>
            <Typography
              sx={{
                fontFamily: special ? "Playfair Display" : "Inter",
                fontSize: special ? "1.35rem" : "1.05rem",
                fontStyle: special ? "italic" : "normal",
                letterSpacing: special ? "0.02em" : "0.04em",
                color: special ? "#F5F5F5" : "#111",
                mb: 1.2,
                textShadow: special ? "0 6px 20px rgba(0,0,0,0.6)" : "none",
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: special ? "rgba(255,255,255,0.7)" : "#6F6F6F",
                maxWidth: "85%",
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </NavLink>
  );
}
